/**
 * Eligibility check dedup / "pick the winner" logic.
 *
 * Why this exists: a single client + insurance plan can have many
 * eligibility_checks rows over its lifetime — Availity SFTP polled
 * yesterday, NEHEN/TriZetto realtime today, a manual re-check from
 * last week, etc.  When the client chart loads we need to pick ONE
 * representative check per insurance_index (primary / secondary /
 * tertiary) to display in the Overview, the View Benefits dialog,
 * and the per-card eligibility blocks.
 *
 * Picking rules (in order):
 *   1. A "settled" check (status in SETTLED_STATUSES) ALWAYS beats an
 *      "in-flight" check (queued / submitted / processing / pending /
 *      received).  Once we have a real 271 result, we don't fall back
 *      to a stale interim status.
 *   2. Among checks in the same bucket (both settled or both in-flight),
 *      the one with the most recent timestamp wins.  Date is taken
 *      from `response_received_at`, then `response_date`, then
 *      `checked_at`, then `request_sent_at` — the first non-empty
 *      value.
 *
 * IMPORTANT: `completed` and `completed_with_errors` are treated as
 * EQUIVALENT for picking purposes.  Treating them as different
 * statuses caused a long-standing bug where Availity (`completed`)
 * and NEHEN (`completed_with_errors` for plans w/ minor parsing
 * issues) checks "fought" — whichever was processed first won the
 * map slot regardless of recency.  Bucket equivalence + date
 * comparison fixes that.
 */

export const SETTLED_STATUSES = new Set(['completed', 'completed_with_errors']);

/** First non-empty timestamp on a check.  All callers should use this so
 *  comparison is consistent with what the History endpoint surfaces. */
export const dateOfCheck = (c) =>
  c?.response_received_at || c?.response_date || c?.checked_at || c?.request_sent_at || '';

/** True if this check has a real 271 result (parsed_data populated, status settled). */
export const isSettled = (c) => SETTLED_STATUSES.has(c?.status);

/**
 * Compare two checks; return TRUE if `candidate` should replace `existing`
 * as the representative check for an insurance_index slot.
 * Pure function — no side effects.
 */
export const isBetterCheck = (candidate, existing) => {
  if (!existing) return true;
  const candSettled = isSettled(candidate);
  const existSettled = isSettled(existing);
  if (candSettled && !existSettled) return true;       // settled beats in-flight
  if (!candSettled && existSettled) return false;       // in-flight never beats settled
  return dateOfCheck(candidate) > dateOfCheck(existing); // both same bucket → newest wins
};

/**
 * Build a map keyed by `insurance_index` → best check for that slot.
 * Used by per-insurance card rendering on the client chart.
 *
 * @param {Array} checks   raw history list from /clients/{id}/eligibility-history
 * @param {string} [clientId]  if passed, stamps client_id on each picked
 *                              entry so consumers can sanity-check it
 *                              belongs to the currently-selected chart
 *                              before reading it (defense-in-depth
 *                              against stale closures racing chart
 *                              switches).
 */
export const buildEligibilityDetailMap = (checks, clientId) => {
  const map = {};
  for (const c of (checks || [])) {
    const idx = c?.insurance_index ?? 0;
    if (isBetterCheck(c, map[idx])) {
      map[idx] = clientId ? { ...c, client_id: clientId } : c;
    }
  }
  return map;
};

/**
 * Build a map keyed by normalized payer/company NAME → best check.
 * Jun 2026: used as the fallback binding for legacy checks that lack
 * `insurance_row_id`. Unlike the positional index map, a name binding
 * survives priority reorders (a plan's checks stay with its company
 * name), so a reordered plan can't inherit another plan's eligibility.
 */
export const normInsName = (s) => (s || '').trim().toUpperCase();

/**
 * Extract the payer-reported plan/product name (EB05 "Plan Coverage
 * Description", e.g. "MASSHEALTH STANDARD") from a check's parsed 271.
 * Prefers the Active Coverage (EB*1) row, falls back to any benefit
 * row carrying a description.
 */
export const getPlanCoverageName = (check) => {
  const benefits = check?.parsed_data?.service_benefits;
  if (!Array.isArray(benefits) || benefits.length === 0) return null;
  const active = benefits.find((b) => b?.status === '1' && b?.plan_coverage_description);
  if (active) return active.plan_coverage_description;
  const any = benefits.find((b) => b?.plan_coverage_description);
  return any ? any.plan_coverage_description : null;
};

export const buildEligibilityDetailMapByName = (checks, clientId) => {
  const map = {};
  for (const c of (checks || [])) {
    const key = normInsName(c?.insurance_name);
    if (!key) continue;
    if (isBetterCheck(c, map[key])) {
      map[key] = clientId ? { ...c, client_id: clientId } : c;
    }
  }
  return map;
};

/**
 * Build a map keyed by the insurance row's STABLE id
 * (`insurance_row_id`) → best check for that plan. Jun 2026: preferred
 * over the positional index map because it survives priority reorders.
 * Only checks that carry `insurance_row_id` participate (legacy checks
 * without it fall back to the index map at the call site).
 */
export const buildEligibilityDetailMapByInsuranceId = (checks, clientId) => {
  const map = {};
  for (const c of (checks || [])) {
    const rowId = c?.insurance_row_id;
    if (!rowId) continue;
    if (isBetterCheck(c, map[rowId])) {
      map[rowId] = clientId ? { ...c, client_id: clientId } : c;
    }
  }
  return map;
};

/**
 * Pick the single check to surface as the headline result on the
 * Client Overview / View Benefits dialog.  Defaults to the primary
 * (insurance_index 0); falls back to ANY settled check; finally to
 * whatever exists.
 *
 * @param {Array} checks    history rows
 * @param {string} [clientId]  when passed, stamps `client_id` on the
 *                              returned object so the consuming
 *                              hook's safe-view filter can verify the
 *                              detail belongs to the currently-selected
 *                              chart (defense against chart-switch
 *                              races leaking stale data).  Without this
 *                              the safe view used to fall through its
 *                              "trust if no client_id" branch and
 *                              briefly render the previous client's
 *                              eligibility on the next chart.
 */
export const pickPrimaryEligibility = (checks, clientId) => {
  if (!checks?.length) return null;
  const map = buildEligibilityDetailMap(checks, clientId);
  const fallback =
    map[0] ||
    checks.find((c) => isSettled(c)) ||
    checks[0] ||
    null;
  if (!fallback) return null;
  return clientId ? { ...fallback, client_id: clientId } : fallback;
};
